import { Rect } from "../../common/rect.js";
import { randomRange } from "../../common/common_funcs.js";
import { CapFit, IImageNineSliceJson, IImageNineSliceRule, SliceMode } from "../../types/nine_slice_interfaces.js";

export interface CornerSize
{
  width: number;
  height: number;
}

export interface CornerSizes
{
  top_left: CornerSize;
  bottom_right: CornerSize;
}

export class NineSliceDrawer
{
  private static nine_slice_config: IImageNineSliceJson = {} as IImageNineSliceJson;
  private static stitch = 0;

  static async init()
  {
    const random_seed = randomRange( 0, 100000000 );
    try
    {
      this.nine_slice_config = await this.loadFromJson(
        `/configs/nine_slice_config.json?v=${random_seed}`
      );
      this.stitch = (() => {
        const is_ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        return is_ios ? 0 : 1;
      })();

    } catch ( error )
    {
      console.error( "Error loading NineSlice config:", error );
      this.nine_slice_config = {} as IImageNineSliceJson;
    }
  }

  private static async loadFromJson( url: string ): Promise<IImageNineSliceJson>
  {
    const response = await fetch( url );
    if ( !response.ok )
    {
      throw new Error( `Failed to load config: ${response.statusText}` );
    }
    return response.json();
  }

  private static adaptCornerSizesToFit(
    sizes: CornerSizes,
    available_width: number,
    available_height: number,
    slice_mode: SliceMode = SliceMode.BOTH,
    rule: IImageNineSliceRule,
  ): CornerSizes
  {

    let scale = 1.0;

    // CapFit.IMAGE (типово): щойно елемент менший за ассет — зменшуємо кути
    // разом з усім малюнком. CapFit.CAPS: кути лишаються авторського розміру,
    // доки вони фізично влазять у елемент (див. клемп нижче).
    const fits_to_image = ( rule.cap_fit ?? CapFit.IMAGE ) === CapFit.IMAGE;

    if ( fits_to_image && slice_mode === SliceMode.BOTH )
    {
      const design_width = LayoutScaler.widthFromDesign( rule.image_width );
      const design_height = LayoutScaler.heightFromDesign( rule.image_height );

      let horizontal_scale = 1.0;
      let vertical_scale = 1.0;

      if ( design_width > available_width && available_width > 0 )
      {
        horizontal_scale = available_width / design_width;
      }

      if ( design_height > available_height && available_height > 0 )
      {
        vertical_scale = available_height / design_height;
      }

      scale = Math.min( horizontal_scale, vertical_scale );
    }
    else if ( fits_to_image && slice_mode === SliceMode.HORIZONTAL )
    {
      const design_width = LayoutScaler.widthFromDesign( rule.image_width );

      if ( design_width > available_width && available_width > 0 )
      {
        scale = available_width / design_width;
      }
    }
    else if ( fits_to_image && slice_mode === SliceMode.VERTICAL )
    {
      const design_height = LayoutScaler.heightFromDesign( rule.image_height );

      if ( design_height > available_height && available_height > 0 )
      {
        scale = available_height / design_height;
      }
    }

    const adjusted_sizes = scale < 1.0 ? {
      top_left: {
        width: Math.round( sizes.top_left.width * scale ),
        height: Math.round( sizes.top_left.height * scale )
      },
      bottom_right: {
        width: Math.round( sizes.bottom_right.width * scale ),
        height: Math.round( sizes.bottom_right.height * scale )
      }
    } : sizes;

    const total_corner_width = adjusted_sizes.top_left.width + adjusted_sizes.bottom_right.width;
    const total_corner_height = adjusted_sizes.top_left.height + adjusted_sizes.bottom_right.height;

    // Кути, що не влазять, стискаємо одним коефіцієнтом по обох осях — інакше
    // радіус кута спотворюється. Рахуємо його лише по тих осях, які реально
    // використовує slice_mode: горизонтальний зріз бере тільки ширини кутів,
    // вертикальний — тільки висоти.
    let fit_scale = 1.0;

    if ( slice_mode !== SliceMode.VERTICAL
      && available_width > 0
      && total_corner_width > available_width )
    {
      fit_scale = Math.min( fit_scale, available_width / total_corner_width );
    }

    if ( slice_mode !== SliceMode.HORIZONTAL
      && available_height > 0
      && total_corner_height > available_height )
    {
      fit_scale = Math.min( fit_scale, available_height / total_corner_height );
    }

    if ( fit_scale < 1.0 )
    {
      adjusted_sizes.top_left.width = Math.round( adjusted_sizes.top_left.width * fit_scale );
      adjusted_sizes.top_left.height = Math.round( adjusted_sizes.top_left.height * fit_scale );
      adjusted_sizes.bottom_right.width = Math.round( adjusted_sizes.bottom_right.width * fit_scale );
      adjusted_sizes.bottom_right.height = Math.round( adjusted_sizes.bottom_right.height * fit_scale );
    }

    return adjusted_sizes;
  }

  static drawNineSliceImage(
    ctx: CanvasRenderingContext2D,
    image: ImageBitmap,
    rule_key: string,
    x: number,
    y: number,
    w: number,
    h: number,
    sizes?: CornerSizes,
    draw_middle = true
  )
  {
    w = Math.floor( w );
    h = Math.floor( h );

    if ( !this.nine_slice_config )
    {
      console.warn( `NineSliceDrawer: nine_slice_config not initialized when drawing '${rule_key}'` );
      return;
    }

    const rule = this.nine_slice_config[rule_key];
    if ( !rule )
    {
      console.warn( `Unknown rule: ${rule_key}` );
      return;
    }
    if ( !sizes )
    {

      sizes = {
        top_left: {
          width: LayoutScaler.cornerRadiusFromDesign( rule.x1 ),
          height: LayoutScaler.cornerRadiusFromDesign( rule.y1 )
        },
        bottom_right: {
          width: LayoutScaler.cornerRadiusFromDesign( rule.image_width - rule.x2 ),
          height: LayoutScaler.cornerRadiusFromDesign( rule.image_height - rule.y2 )
        }
      };

      sizes = this.adaptCornerSizesToFit( sizes, w, h, rule.slice_mode, rule);
    }

    let top_left_width      = sizes.top_left.width;
    let top_left_height     = sizes.top_left.height;
    let bottom_right_width  = sizes.bottom_right.width;
    let bottom_right_height = sizes.bottom_right.height;

    if ( top_left_width      === -1 && top_left_height     > 0 ) top_left_width      = top_left_height;
    if ( top_left_height     === -1 && top_left_width      > 0 ) top_left_height     = top_left_width;
    if ( bottom_right_width  === -1 && bottom_right_height > 0 ) bottom_right_width  = bottom_right_height;
    if ( bottom_right_height === -1 && bottom_right_width  > 0 ) bottom_right_height = bottom_right_width;

    const top_right_width    = bottom_right_width;
    const top_right_height   = top_left_height;
    const bottom_left_width  = top_left_width;
    const bottom_left_height = bottom_right_height;

    const to_px = ( value: number, total: number ) =>
      Math.floor( value <= 1 ? value * total : value );

    const cap_tl_w = to_px( top_left_width, w );
    const cap_tl_h = to_px( top_left_height, h );
    const cap_tr_w = to_px( top_right_width, w );
    const cap_tr_h = to_px( top_right_height, h );
    const cap_bl_w = to_px( bottom_left_width, w );
    const cap_bl_h = to_px( bottom_left_height, h );
    const cap_br_w = to_px( bottom_right_width, w );
    const cap_br_h = to_px( bottom_right_height, h );

    switch ( rule.slice_mode )
    {
      case SliceMode.HORIZONTAL:
        this.drawHorizontalSlice(
          ctx,
          image,
          rule,
          x,
          y,
          w,
          h,
          cap_tl_w,
          cap_br_w
        );
        break;
      case SliceMode.VERTICAL:
        this.drawVerticalSlice(
          ctx,
          image,
          rule,
          x,
          y,
          w,
          h,
          cap_tl_h,
          cap_br_h
        );
        break;
      case SliceMode.BOTH:
      default:
        this.drawBothSlices(
          ctx,
          image,
          rule,
          x,
          y,
          w,
          h,
          cap_tl_w,
          cap_tl_h,
          cap_tr_w,
          cap_tr_h,
          cap_bl_w,
          cap_bl_h,
          cap_br_w,
          cap_br_h,
          draw_middle
        );
        break;
    }
  }

  private static drawHorizontalSlice(
    ctx: CanvasRenderingContext2D,
    image: ImageBitmap,
    rule: IImageNineSliceRule,
    x: number,
    y: number,
    w: number,
    h: number,
    left_cap: number,
    right_cap: number
  )
  {
    const stitch_size = this.stitch;
    const x0 = Math.floor( x );
    const x1 = x0 + left_cap - stitch_size;
    const x2 = x0 + w - right_cap;
    const mid_w = Math.floor( w - left_cap - right_cap + 2 * stitch_size );

    const dest_l = new Rect( x0, y, left_cap, h );
    const dest_m = new Rect( x1, y, mid_w, h );
    const dest_r = new Rect( x2, y, right_cap, h );

    const src_w1 = rule.x1;
    const src_w2 = rule.x2 - rule.x1;
    const src_w3 = rule.image_width - rule.x2;
    const src_l = new Rect( 0, 0, src_w1, rule.image_height );
    const src_m = new Rect( rule.x1, 0, src_w2, rule.image_height );
    const src_r = new Rect( rule.x2, 0, src_w3, rule.image_height );

    this.drawImage( ctx, image, dest_l, src_l );
    this.drawImage( ctx, image, dest_m, src_m );
    this.drawImage( ctx, image, dest_r, src_r );
  }

  private static drawVerticalSlice(
    ctx: CanvasRenderingContext2D,
    image: ImageBitmap,
    rule: IImageNineSliceRule,
    x: number,
    y: number,
    w: number,
    h: number,
    top_cap: number,
    bottom_cap: number
  )
  {
    const stitch_size = this.stitch;
    const y0 = Math.floor( y );
    const y1 = y0 + top_cap - stitch_size;
    const y2 = y0 + h - bottom_cap;
    const mid_h = Math.floor( h - top_cap - bottom_cap + 2 * stitch_size );

    const dest_t = new Rect( x, y0, w, top_cap );
    const dest_m = new Rect( x, y1, w, mid_h );
    const dest_b = new Rect( x, y2, w, bottom_cap );

    const src_h1 = rule.y1;
    const src_h2 = rule.y2 - rule.y1;
    const src_h3 = rule.image_height - rule.y2;
    const src_t = new Rect( 0, 0, rule.image_width, src_h1 );
    const src_m = new Rect( 0, rule.y1, rule.image_width, src_h2 );
    const src_b = new Rect( 0, rule.y2, rule.image_width, src_h3 );

    this.drawImage( ctx, image, dest_t, src_t );
    this.drawImage( ctx, image, dest_m, src_m );
    this.drawImage( ctx, image, dest_b, src_b );
  }

  private static drawBothSlices(
    ctx: CanvasRenderingContext2D,
    image: ImageBitmap,
    rule: IImageNineSliceRule,
    x: number,
    y: number,
    w: number,
    h: number,
    cap_tl_w: number,
    cap_tl_h: number,
    cap_tr_w: number,
    cap_tr_h: number,
    cap_bl_w: number,
    cap_bl_h: number,
    cap_br_w: number,
    cap_br_h: number,
    draw_middle: boolean
  )
  {
    const stitch_size = this.stitch;
    const x0 = Math.floor( x );
    const y0 = Math.floor( y );
    const x1 = x0 + cap_tl_w - stitch_size;
    const y1 = y0 + cap_tl_h - stitch_size;
    const x2 = x0 + w - cap_tr_w;
    const y2 = y0 + h - cap_br_h;

    const mid_w = Math.floor( w - cap_tl_w - cap_tr_w + 2 * stitch_size );
    const mid_h = Math.floor( h - cap_tl_h - cap_br_h + 2 * stitch_size );

    const dest_rects = {
      top_left:      new Rect( x0, y0, cap_tl_w, cap_tl_h ),
      top_center:    new Rect( x1, y0, mid_w,    cap_tl_h ),
      top_right:     new Rect( x2, y0, cap_tr_w, cap_tr_h ),
      left_center:   new Rect( x0, y1, cap_tl_w, mid_h ),
      center:        new Rect( x1, y1, mid_w,    mid_h ),
      right_center:  new Rect( x2, y1, cap_tr_w, mid_h ),
      bottom_left:   new Rect( x0, y2, cap_bl_w, cap_bl_h ),
      bottom_center: new Rect( x1, y2, mid_w,    cap_bl_h ),
      bottom_right:  new Rect( x2, y2, cap_br_w, cap_br_h ),
    };

    const src_rects = {
      seg1: new Rect( 0,       0,       rule.x1,                    rule.y1 ),
      seg2: new Rect( rule.x1, 0,       rule.x2 - rule.x1,          rule.y1 ),
      seg3: new Rect( rule.x2, 0,       rule.image_width - rule.x2, rule.y1 ),
      seg4: new Rect( 0,       rule.y1, rule.x1,                    rule.y2 - rule.y1 ),
      seg5: new Rect( rule.x1, rule.y1, rule.x2 - rule.x1,          rule.y2 - rule.y1 ),
      seg6: new Rect( rule.x2, rule.y1, rule.image_width - rule.x2, rule.y2 - rule.y1 ),
      seg7: new Rect( 0,       rule.y2, rule.x1,                    rule.image_height - rule.y2 ),
      seg8: new Rect( rule.x1, rule.y2, rule.x2 - rule.x1,          rule.image_height - rule.y2 ),
      seg9: new Rect( rule.x2, rule.y2, rule.image_width - rule.x2, rule.image_height - rule.y2 ),
    };

    this.drawImage( ctx, image, dest_rects.top_left,    src_rects.seg1 );
    this.drawImage( ctx, image, dest_rects.top_center,  src_rects.seg2 );
    this.drawImage( ctx, image, dest_rects.top_right,   src_rects.seg3 );
    this.drawImage( ctx, image, dest_rects.left_center, src_rects.seg4 );
    if ( draw_middle ) this.drawImage( ctx, image, dest_rects.center, src_rects.seg5 );
    this.drawImage( ctx, image, dest_rects.right_center,  src_rects.seg6 );
    this.drawImage( ctx, image, dest_rects.bottom_left,   src_rects.seg7 );
    this.drawImage( ctx, image, dest_rects.bottom_center, src_rects.seg8 );
    this.drawImage( ctx, image, dest_rects.bottom_right,  src_rects.seg9 );
  }

  private static drawImage(
    ctx: CanvasRenderingContext2D,
    image: ImageBitmap,
    dest: Rect,
    src: Rect
  )
  {
    ctx.drawImage(
      image,
      src.getX(), src.getY(), src.getW(), src.getH(),
      dest.getX(), dest.getY(), dest.getW(), dest.getH()
    );
  }
}
